"use client";

import { useEffect, useState, useRef } from "react";

// Client-side session memory cache
const clientTranslationCache = new Map<string, string>();

// Request queue for batching translations and debouncing
let pendingQueue: { text: string; resolve: (val: string) => void }[] = [];
let batchTimeout: NodeJS.Timeout | null = null;

export function queueTranslate(text: string): Promise<string> {
  const trimmed = text?.trim();
  if (!trimmed) return Promise.resolve(text);

  const cacheKey = `en:${trimmed}`;
  if (clientTranslationCache.has(cacheKey)) {
    return Promise.resolve(clientTranslationCache.get(cacheKey)!);
  }

  return new Promise((resolve) => {
    pendingQueue.push({ text: trimmed, resolve });

    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }

    batchTimeout = setTimeout(processBatchQueue, 40);
  });
}

/**
 * Reusable hook to translate single text or small strings (e.g. titles, tags, headers)
 */
export function useTranslatedText(text: string = "", locale: string = "vi"): string {
  const isEn = locale === "en";
  const trimmed = text?.trim() || "";

  const [translated, setTranslated] = useState<string>(() => {
    if (!isEn || !trimmed) return text;
    return clientTranslationCache.get(`en:${trimmed}`) || text;
  });

  useEffect(() => {
    if (!isEn || !trimmed) {
      setTranslated(text);
      return;
    }

    const cached = clientTranslationCache.get(`en:${trimmed}`);
    if (cached) {
      setTranslated(cached);
      return;
    }

    let isCancelled = false;
    queueTranslate(trimmed).then((res) => {
      if (!isCancelled) {
        setTranslated(res);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [text, isEn, trimmed]);

  return translated;
}

async function processBatchQueue() {
  if (pendingQueue.length === 0) return;

  const currentBatch = [...pendingQueue];
  pendingQueue = [];
  batchTimeout = null;

  // Distinct texts to translate
  const uniqueTexts = Array.from(new Set(currentBatch.map((item) => item.text)));

  try {
    const res = await fetch("/api/translate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        texts: uniqueTexts,
        from: "vi",
        to: "en",
      }),
    });

    if (res.ok) {
      const data = await res.json();
      const translations: string[] = data?.translations || [];

      const transMap = new Map<string, string>();
      uniqueTexts.forEach((text, i) => {
        const trans = translations[i] || text;
        clientTranslationCache.set(`en:${text}`, trans);
        transMap.set(text, trans);
      });

      currentBatch.forEach((item) => {
        item.resolve(transMap.get(item.text) || item.text);
      });
      return;
    }
  } catch (err) {
    console.error("Batch translate error:", err);
  }

  // Fallback if failed
  currentBatch.forEach((item) => {
    item.resolve(item.text);
  });
}

export interface ProductTranslationFields {
  title?: string;
  summary?: string;
  packaging?: string;
  category?: string;
  description?: string;
}

export function useProductTranslation(
  fields: ProductTranslationFields,
  locale: string = "vi"
) {
  const isEn = locale === "en";

  // Check cache immediately on first render if possible
  const getInitialState = () => {
    if (!isEn) return fields;
    return {
      title: (fields.title && clientTranslationCache.get(`en:${fields.title.trim()}`)) || fields.title,
      summary: (fields.summary && clientTranslationCache.get(`en:${fields.summary.trim()}`)) || fields.summary,
      packaging: (fields.packaging && clientTranslationCache.get(`en:${fields.packaging.trim()}`)) || fields.packaging,
      category: (fields.category && clientTranslationCache.get(`en:${fields.category.trim()}`)) || fields.category,
      description: (fields.description && clientTranslationCache.get(`en:${fields.description.trim()}`)) || fields.description,
    };
  };

  const [translated, setTranslated] = useState<ProductTranslationFields>(getInitialState);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  useEffect(() => {
    if (!isEn) {
      setTranslated(fields);
      setIsTranslating(false);
      return;
    }

    const {
      title = "",
      summary = "",
      packaging = "",
      category = "",
      description = "",
    } = fields;

    // Check if everything is already cached
    const items = [
      { key: "title", text: title },
      { key: "summary", text: summary },
      { key: "packaging", text: packaging },
      { key: "category", text: category },
      { key: "description", text: description },
    ];

    let hasUncached = false;
    const currentResolved: any = {};

    items.forEach((item) => {
      if (!item.text) {
        currentResolved[item.key] = "";
      } else {
        const cached = clientTranslationCache.get(`en:${item.text.trim()}`);
        if (cached) {
          currentResolved[item.key] = cached;
        } else {
          currentResolved[item.key] = item.text;
          hasUncached = true;
        }
      }
    });

    setTranslated(currentResolved);

    if (!hasUncached) {
      setIsTranslating(false);
      return;
    }

    setIsTranslating(true);
    let isCancelled = false;

    Promise.all(
      items.map(async (item) => {
        if (!item.text) return { key: item.key, text: "" };
        const translatedText = await queueTranslate(item.text);
        return { key: item.key, text: translatedText };
      })
    ).then((results) => {
      if (isCancelled) return;
      const nextState: any = {};
      results.forEach((r) => {
        nextState[r.key] = r.text;
      });
      setTranslated(nextState);
      setIsTranslating(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [
    isEn,
    fields.title,
    fields.summary,
    fields.packaging,
    fields.category,
    fields.description,
  ]);

  return {
    ...translated,
    isTranslating,
  };
}
